import { FileOutlined, UploadOutlined } from "@ant-design/icons";
import { Pencil, Trash2 } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { Actions } from "../../tabs/Actions/Actions";
import CoreModal, { CoreModalProps } from "../CoreModal/index";
import { CoreAddModalProps } from "../ShipmentModals/AddShipmentModal";
import { CoreEditModalProps } from "../ShipmentModals/EditShipmentModal";
import styles from "./styles.module.scss";

interface UploadCSVModalProps<TData, TOptions> extends CoreModalProps {
  ManualModal: React.ComponentType<CoreAddModalProps<TData, TOptions>>;
  EditModal: React.ComponentType<CoreEditModalProps<TData, TOptions>>;
  onSaveData: (data: TData[]) => void;
  data: TData[];
}

type FileType = File & { uploadedTime: Date };

const isValidCSV = (file: File): boolean => {
  return file.type === "text/csv";
};

export const UploadCSVModal = <TData, TOptions>({
  open,
  onCancel,
  title,
  ManualModal,
  EditModal,
  onSaveData,
  data,
}: UploadCSVModalProps<TData, TOptions>) => {
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(0);
  const [filesToupload, setFilesToUpload] = useState<FileType[]>([]);
  const [error, setError] = useState<string | null>(null); // состояние для ошибки

  const toFileTypeArray = (fileList: FileList): FileType[] =>
    Array.from(fileList).map((file) =>
      Object.assign(file, { uploadedTime: new Date() }),
    );

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    if (!isValidCSV(file)) {
      setError("Please upload a valid CSV file.");
      return;
    }
    setError(null);
    setFilesToUpload((prev) => [...prev, ...toFileTypeArray(fileList)]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    if (!files || files.length === 0) return;

    const csvFiles = Array.from(files).filter(isValidCSV);

    if (csvFiles.length < files.length) {
      setError("Please upload only CSV files.");
    } else {
      setError(null);
    }

    if (csvFiles.length > 0) {
      setFilesToUpload((prev) => [
        ...prev,
        ...csvFiles.map((file) =>
          Object.assign(file, { uploadedTime: new Date() }),
        ),
      ]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) =>
    e.preventDefault();

  const removeFile = (file: FileType) =>
    setFilesToUpload((prev) =>
      prev.filter((f) => JSON.stringify(f) !== JSON.stringify(file)),
    );

  const closeManual = () => setIsManualOpen(false);
  const closeEdit = () => setIsEditOpen(false);
  const cancelAll = () => {
    onCancel();
    closeManual();
    closeEdit();
  };

  const onSaveClick = () => {
    setEditIdx(0);
    setIsEditOpen(true);
  };

  const onEditClick = (idx: number) => {
    setEditIdx(idx);
    setIsEditOpen(true);
  };

  const onEditModalSaveClick = (data: TData, index: number) => {
    onSaveData([data]);
    cancelAll();
  };

  const onSaveManualClick = (data: TData[]) => {
    onSaveData(data);
    cancelAll();
  };

  return (
    <>
      <CoreModal
        title={title}
        open={open}
        onCancel={cancelAll}
        onSave={onSaveClick}
        disabledButton={filesToupload.length === 0}
      >
        <div className={styles.container}>
          <div
            className={styles.uploadBox}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <UploadOutlined className={styles.uploadIcon} />
            <label htmlFor="file-upload" className={styles.fakeLabel}>
              Drag & drop files or{" "}
              <span className={styles.linkText}>Browse</span>
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".csv"
              className={styles.hiddenInput}
              onChange={handleFileChange}
            />
            <p className={styles.supportText}>Supported format: CSV</p>
            {error && <p style={{ color: "red" }}>{error}</p>}
          </div>
          <p className={styles.or}>
            or{" "}
            <span
              className={styles.manual}
              onClick={() => setIsManualOpen(true)}
            >
              add manual
            </span>
          </p>
        </div>
        {filesToupload.length > 0 && (
          <>
            <p className={styles.uploaded}>
              Uploaded - {filesToupload.length} file{" "}
              {filesToupload.length > 1 ? "s" : ""}
            </p>
            <div className={styles.filesContainer}>
              {filesToupload.map((f, idx) => (
                <div key={idx} className={styles.fileContaier}>
                  <div className={styles.general}>
                    <FileOutlined className={styles.icon} />
                    <div className={styles.fileInfo}>
                      <p className={styles.name}>{f.name}</p>
                      <p className={styles.time}>
                        uploaded on {f.uploadedTime.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Actions
                    record={undefined}
                    classname={styles.actions}
                    actions={[
                      {
                        icon: <Pencil />,
                        onClick: () => onEditClick(idx),
                      },
                      {
                        icon: <Trash2 />,
                        onClick: () => removeFile(f),
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </CoreModal>
      <ManualModal
        open={isManualOpen}
        onCancel={cancelAll}
        onBack={closeManual}
        onSaveData={onSaveManualClick}
      />
      {filesToupload.length > 0 && (
        <EditModal
          filename={filesToupload[editIdx].name}
          open={isEditOpen}
          onCancel={cancelAll}
          onBack={closeEdit}
          data={Array.from({ length: filesToupload.length }, () => data).flat()}
          index={editIdx}
          onPrev={() => setEditIdx((i) => i - 1)}
          onNext={() => setEditIdx((i) => i + 1)}
          onSaveData={onEditModalSaveClick}
        />
      )}
    </>
  );
};

export default UploadCSVModal;
